/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-empty-pattern */
/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { addYears, format, isBefore, subDays } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import DatePickerField from "components/lib/Shared/DatePickerField";
import { Field, Form, Formik, useFormikContext, getIn } from "formik";
import Button from "components/lib/Shared/Button";
import { ChevronLeftIcon } from "@heroicons/react/outline";
import Input from "components/lib/Shared/Input";
import ReactSelectWithFormik from "components/lib/Shared/DropdownSelect";
import { getFullName } from "utils";
import { Constants } from "config/constants";
import { annualReturnsSchema } from "./validations/annualReturnValidation";
import { ExclamationIcon } from "@heroicons/react/solid";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import ArrowPath from "components/lib/Shared/Icons/ArrowPath";
import { generateAnnualReturnAsync } from "state/slices/generateAnnualReturn";

const generateAnnualReturn = async ({
  companyId,
  annualReturnDate,
  dateOfReturn,
  indebtedness,
  signatory,
  dispatch,
}) => {
  ///make API call
  try {
    const payload = {
      companyId,
      annualReturnDate,
      dateOfReturn,
      indebtedness,
      signatory,
    };

    const response = await dispatch(
      generateAnnualReturnAsync({ data: payload })
    ).unwrap();

    return response;
  } catch (error) { }

  return {
    success: true,
    data: {
      fileName: `${Date.now()}annual-return-${annualReturnDate.replaceAll(
        "/",
        "-"
      )}.pdf`,
      generatedAt: new Date().toISOString(),
      summary: {
        annualReturnDate,
        dateOfReturn,
        indebtedness,
        signatoryName: signatory?.label,
      },
    },
  };
};

const useDebouncedValue = (value, delay = 700) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return debouncedValue;
};

const AnnualReturnRow = ({
  option,
  index,
  signatories,
  companyId,
  generatedReturns,
  setGeneratedReturns,
}) => {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    validateForm,
    setFieldTouched,
    isSubmitting,
  } = useFormikContext();

  const dispatch = useDispatch();

  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState("");

  const base = `annualReturns.returns[${index}]`;
  const dateName = `${base}.dateOfReturn`;
  const indebtednessName = `${base}.indebtedness`;
  const signatoryName = `${base}.signatory`;
  const fileUploadName = `${base}.fileUploads`;

  const currentRow = getIn(values, base) || {};
  const dateOfReturn = currentRow?.dateOfReturn;
  const indebtedness = currentRow?.indebtedness;
  const signatory = currentRow?.signatory;

  const debouncedIndebtedness = useDebouncedValue(indebtedness, 1000);

  const [yearStr, monthStr, dayStr] = option?.value?.split("/");
  const centralDate = new Date(`${yearStr}-${monthStr}-${dayStr}`);
  const isValidCentralDate = !isNaN(centralDate.getTime());

  const minDate = isValidCentralDate ? subDays(centralDate, 28) : null;
  const maxDate = isValidCentralDate ? centralDate : null;
  const year = option.label.split("/")[0];

  const isComplete = Boolean(
    dateOfReturn && debouncedIndebtedness?.toString()?.trim() && signatory
  );

  const generationPayload = useMemo(
    () => ({
      annualReturnDate: option.value,
      dateOfReturn,
      indebtedness: debouncedIndebtedness,
      signatoryId: signatory?.value,
    }),
    [option.value, dateOfReturn, debouncedIndebtedness, signatory]
  );

  const lastGeneratedPayloadRef = useRef("");

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setGenerationError("");

      if (!isComplete) return;
      const formErrors = await validateForm();
      const rowErrors = getIn(formErrors, base);

      const hasRowErrors =
        rowErrors &&
        (rowErrors.dateOfReturn ||
          rowErrors.indebtedness ||
          rowErrors.signatory);

      if (hasRowErrors) return;

      const payloadKey = JSON.stringify(generationPayload);

      if (lastGeneratedPayloadRef.current === payloadKey) {
        return;
      }

      try {
        setIsGenerating(true);

        const response = await generateAnnualReturn({
          companyId,
          annualReturnDate: option.value,
          dateOfReturn,
          indebtedness: debouncedIndebtedness,
          signatory,
          dispatch,
        });

        if (cancelled) return;

        setGeneratedReturns((prev) => ({
          ...prev,
          [index]: response,
        }));

        setFieldValue(
          `${fileUploadName}.annualReturns`,
          response?.fileName || ""
        );

        lastGeneratedPayloadRef.current = payloadKey;
      } catch (error) {
        if (cancelled) return;
        setGenerationError(
          error?.message || "Failed to generate annual return."
        );
      } finally {
        if (!cancelled) setIsGenerating(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    base,
    companyId,
    dateOfReturn,
    debouncedIndebtedness,
    signatory,
    isComplete,
    generationPayload,
    index,
    option.value,
    setGeneratedReturns,
    validateForm,
    dispatch,
  ]);

  const generatedReturn = generatedReturns[index];

  return (
    <div className="shadow-sm sm:rounded-md px-4 py-6 space-y-4 bg-white sm:p-6 mt-4">
      <div className="mb-4">
        <h2 className="text-4xl font-bold text-gray-800">{year}</h2>
        <p className="text-gray-800 font-bold">Annual Return</p>
      </div>
      <div className="mt-4">
        <DatePickerField
          label="Date of this Return"
          onChange={(date) => {
            if (!date) {
              setFieldValue(dateName, "");
              return;
            }

            if (isNaN(date.getTime())) {
              console.error("Invalid date:", date);
              return;
            }

            const formattedDate = format(date, "yyyy/MM/dd");
            setFieldValue(dateName, formattedDate);
            setFieldTouched(dateName, true, true);
          }}
          selected={dateOfReturn ? new Date(dateOfReturn) : null}
          name={dateName}
          placeholderText="Enter Return date"
          errorPath={dateName}
          maxDate={maxDate}
          minDate={minDate}
          disabled={isSubmitting || isGenerating}
          showRequiredAsterisk
        />
        <ValidationError
          errorMessage={getIn(errors, `${base}.dateOfReturn`)}
          isTouched={getIn(touched, `${base}.dateOfReturn`)}
        />
      </div>

      <div>
        <Field
          label="Indebtedness"
          name={indebtednessName}
          component={Input}
          placeholder="Enter Indebtedness"
          showRequiredAsterisk
        />
        <ValidationError
          errorMessage={getIn(errors, `${base}.indebtedness`)}
          isTouched={getIn(touched, `${base}.indebtedness`)}
        />
      </div>

      <div className="w-full block">
        <Field
          label="Select Signatory for this Return"
          placeholder="Select Signatory"
          name={signatoryName}
          component={ReactSelectWithFormik}
          options={signatories}
          showRequiredAsterisk
        />
        <ValidationError
          errorMessage={getIn(errors, `${base}.signatory`)}
          isTouched={getIn(touched, `${base}.signatory`)}
        />
      </div>

      <div className="mt-4">
        <h3 className=" mb-2 after:content-['*'] after:text-red-500  after:ml-1 after:text-sm ">
          Annual Returns
        </h3>
        {!generatedReturn && (
          <div className="inline-flex items-center bg-green-50 rounded-md text-sm p-3 italic wrap ">
            <ExclamationIcon className="w-5 h-5 mr-2 text-secondary font-bold" />
            Note: Complete the three fields above to generate the annual return
            automatically
          </div>
        )}
        {isGenerating && (
          <div className="p-3 rounded-md bg-blue-50 text-blue-700 text-sm">
            Generating annual return...
          </div>
        )}

        {generationError && (
          <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm">
            {generationError}
          </div>
        )}

        {!isGenerating && generatedReturn && (
          <div className=" flex items-center justify-center p-6 rounded-lg border border-[#B9F8CF] bg-[#F0FDF4] gap-4 ">
            <div className="w-[90%]">
              <Field
                name={fileUploadName}
                component={FileUploadForm}
                documentData={[
                  {
                    label: "Annual Return",
                    name: "annualReturns",
                    accept:
                      ".doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.documen",
                  },
                ]}
                onlyPreview
                showDocumentLabel
                hideResolveAllCheckbox
                resolveAllFieldsCheckbox
                editorKey={generatedReturn.fileName}
                editorParams={{
                  mode: "view",
                  title: "Annual Return",
                }}
              />
            </div>
            <div className="w-[10%] text-sm py-2 flex items-center justify-center gap-2">
              <ArrowPath />
              Regenerate
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AnnualReturns = ({ handleGoForward, handleGoBack }) => {
  const { company } = useSelector((state) => state.companySlice);
  const { companyOfficials } = useSelector(
    (state) => state.companyDashboardSlice
  );

  const [generatedReturns, setGeneratedReturns] = useState({});

  const registrationDate = new Date(company?.registrationDate);
  const registrationYear = parseInt(format(registrationDate, "yyyy"));
  const currentDate = new Date();

  const annualReturnsYearOptions = (() => {
    const options = [];
    let currentAnnualReturnDate = addYears(registrationDate, 1);

    while (
      isBefore(currentAnnualReturnDate, currentDate) ||
      format(currentAnnualReturnDate, "yyyy-MM-dd") ===
      format(currentDate, "yyyy-MM-dd")
    ) {
      const dateStr = format(currentAnnualReturnDate, "yyyy/MM/dd");
      options.push({ label: dateStr, value: dateStr });
      currentAnnualReturnDate = addYears(currentAnnualReturnDate, 1);
    }

    return options;
  })();

  const areAnnualReturnsAvailable = annualReturnsYearOptions.length > 0;
  const nextAnnualReturnDate = areAnnualReturnsAvailable
    ? null
    : format(addYears(registrationDate, 1), "yyyy-MM-dd");

  const signatories = companyOfficials?.items
    ?.filter((official) =>
      [
        Constants.APPLICANT_CAPACITIES.DIRECTOR,
        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER,
        Constants.APPLICANT_CAPACITIES.COMPANY_SECRETARY,
      ].includes(official?.designation)
    )
    .map((official) => ({
      value: official._id,
      label: getFullName(official.names),
      email: official.electronicAddress.emailAddress,
      id: official._id,
      name: getFullName(official.names),
    }));

  const initialValues = {
    annualReturns: {
      returns: annualReturnsYearOptions.map(() => ({
        dateOfReturn: "",
        indebtedness: "",
        signatory: null,
        fileUploads: {},
      })),
    },
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={annualReturnsSchema}
    >
      <Form>
        <div>
          <div className="py-6">
            Year of first annual return{" "}
            <span className="bg-white border py-1 px-4 rounded-md">
              {registrationYear}
            </span>
          </div>

          {!areAnnualReturnsAvailable && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md my-4">
              <p className="text-blue-800 font-medium">
                Annual Returns Not Yet Available
              </p>
              <p className="text-blue-700 text-sm mt-1">
                Your first annual return will be due on {nextAnnualReturnDate}.
                Please check back after this date to file your annual return.
              </p>
            </div>
          )}

          {annualReturnsYearOptions.map((option, index) => (
            <AnnualReturnRow
              key={option.value}
              option={option}
              index={index}
              signatories={signatories}
              companyId={company?._id}
              generatedReturns={generatedReturns}
              setGeneratedReturns={setGeneratedReturns}
            />
          ))}

          <div className="mt-6 flex justify-between gap-4">
            <button
              type="button"
              onClick={() => handleGoBack()}
              className="flex items-start float-right p-2 font-semibold rounded text-primary hover:bg-gray-700 hover:text-white"
            >
              <span className="flex items-center mr-4 h-7">
                <ChevronLeftIcon
                  className="flex-shrink-0 w-5 h-5"
                  aria-hidden="true"
                />
              </span>
              <p className="font-semibold">Go back</p>
            </button>

            <Button
              type="button"
              className="flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus bg-sflPrimary"
            >
              Save and Proceed
            </Button>
          </div>
        </div>
      </Form>
    </Formik>
  );
};

export default AnnualReturns;

const ValidationError = ({ errorMessage, isTouched }) => {
  if (!errorMessage || !isTouched) return null;

  return (
    <div className="text-red-500 flex items-center py-2">
      <svg
        aria-hidden="true"
        className="flex-shrink-0 inline w-5 h-5 mr-1"
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>

      <p className="text-sm">{errorMessage}</p>
    </div>
  );
};
